import React, { useState, useEffect } from 'react';
import { Cookie, X, Check, Settings } from 'lucide-react';
import { Button } from './ui/button';

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      accepted_at: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const acceptNecessaryOnly = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      accepted_at: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const savePreferences = (preferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      ...preferences,
      accepted_at: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
        onClick={() => {}}
      />
      
      {/* Banner */}
      <div className="relative w-full max-w-2xl bg-dark-paper border border-white/10 rounded-lg shadow-2xl pointer-events-auto mb-4 animate-in slide-in-from-bottom duration-500">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-gold/20 rounded-full">
              <Cookie className="w-6 h-6 text-gold" />
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-xl text-white mb-1">
                Nous respectons votre vie privée
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                DiscreetLux utilise des cookies pour améliorer votre expérience, 
                analyser le trafic et personnaliser le contenu. En continuant à naviguer, 
                vous acceptez notre utilisation des cookies conformément à notre{' '}
                <a href="/cgu" className="text-gold hover:text-gold-light underline">
                  politique de confidentialité
                </a>.
              </p>
            </div>
          </div>

          {/* Cookie Details (expandable) */}
          {showDetails && (
            <div className="mb-4 p-4 bg-dark-subtle rounded-lg space-y-3">
              <CookieOption
                title="Cookies essentiels"
                description="Nécessaires au fonctionnement du site (authentification, sécurité)"
                required
                defaultChecked
              />
              <CookieOption
                id="analytics"
                title="Cookies analytiques"
                description="Nous aident à comprendre comment vous utilisez le site"
                defaultChecked
              />
              <CookieOption
                id="marketing"
                title="Cookies marketing"
                description="Utilisés pour vous proposer des contenus pertinents"
                defaultChecked={false}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={acceptNecessaryOnly}
              className="flex-1 bg-dark-subtle hover:bg-dark-subtle/80 text-gray-300 border border-white/10"
            >
              <X className="w-4 h-4 mr-2" />
              Refuser tout
            </Button>
            
            <Button
              onClick={() => setShowDetails(!showDetails)}
              className="flex-1 bg-dark-subtle hover:bg-dark-subtle/80 text-gray-300 border border-white/10"
            >
              <Settings className="w-4 h-4 mr-2" />
              Personnaliser
            </Button>
            
            <Button
              onClick={acceptAll}
              className="flex-1 btn-gold"
            >
              <Check className="w-4 h-4 mr-2" />
              Accepter tout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CookieOption({ id, title, description, required = false, defaultChecked = true }) {
  const [checked, setChecked] = useState(defaultChecked);

  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        {required ? (
          <div className="w-5 h-5 rounded bg-gold/30 flex items-center justify-center">
            <Check className="w-3 h-3 text-gold" />
          </div>
        ) : (
          <button
            onClick={() => setChecked(!checked)}
            className={`w-5 h-5 rounded border transition-colors ${
              checked 
                ? 'bg-gold border-gold' 
                : 'bg-transparent border-white/30 hover:border-white/50'
            } flex items-center justify-center`}
          >
            {checked && <Check className="w-3 h-3 text-dark" />}
          </button>
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-white text-sm font-medium">{title}</span>
          {required && (
            <span className="text-xs text-gray-500">(obligatoire)</span>
          )}
        </div>
        <p className="text-gray-500 text-xs">{description}</p>
      </div>
    </div>
  );
}
